import React from 'react';
import {
  List as MuiList,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  ListProps as MuiListProps,
  Divider,
} from '@mui/material';
import { StyledList, StyledListItem } from './List.style';

export interface ListItemData {
  id: string;
  primary: string;
  secondary?: string;
  icon?: React.ReactNode;
  avatar?: React.ReactNode;
  disabled?: boolean;
  selected?: boolean;
  divider?: boolean;
  onClick?: () => void;
}

export interface ListProps extends MuiListProps {
  /**
   * Array of list items
   */
  items?: ListItemData[];
  /**
   * Whether list items should be dense
   */
  dense?: boolean;
  /**
   * Whether to disable padding
   */
  disablePadding?: boolean;
  /**
   * The selected item ID
   */
  selectedId?: string;
  /**
   * Callback fired when a list item is clicked
   */
  onItemClick?: (id: string, item: ListItemData) => void;
}

export const List: React.FC<ListProps> = ({
  items,
  dense = false,
  disablePadding = false,
  selectedId,
  onItemClick,
  children,
  ...props
}) => {
  const handleItemClick = (item: ListItemData) => {
    if (item.onClick) {
      item.onClick();
    }
    if (onItemClick) {
      onItemClick(item.id, item);
    }
  };

  if (children) {
    return (
      <StyledList dense={dense} disablePadding={disablePadding} {...props}>
        {children}
      </StyledList>
    );
  }

  if (!items) {
    return null;
  }

  return (
    <StyledList dense={dense} disablePadding={disablePadding} {...props}>
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          <StyledListItem disablePadding={!item.icon && !item.avatar}>
            <ListItemButton
              selected={selectedId === item.id || item.selected}
              disabled={item.disabled}
              onClick={() => handleItemClick(item)}
              dense={dense}
            >
              {item.icon && (
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
              )}
              
              {item.avatar && (
                <ListItemAvatar>
                  {item.avatar}
                </ListItemAvatar>
              )}
              
              <ListItemText
                primary={item.primary}
                secondary={item.secondary}
              />
            </ListItemButton>
          </StyledListItem>
          
          {item.divider && index < items.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </StyledList>
  );
}; 